import React, { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { FileBarChart, Send, Loader2, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

// Admin: preview today's Daily Task Report and resend it in one click.
export function TaskReportPreview({ title = "Daily Task Report", base = "/tasks", testid = "task-report", note = "This preview covers the last 24 hours up to now." }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const [sending, setSending] = useState(false);

  const load = () => { setD(null); api.get(`${base}/daily-report-preview`).then((r) => setD(r.data)).catch((e) => { toast.error(formatErr(e.response?.data?.detail)); setOpen(false); }); };
  const show = () => { setOpen(true); load(); };

  async function resend() {
    if (!window.confirm(`Send the ${title} now to ${d.to.join(", ")}?`)) return;
    setSending(true);
    try {
      const r = await api.post(`${base}/send-daily-report-now`);
      r.data.ok ? toast.success(`Report sent to ${d.to.join(", ")}`) : toast.error(`Send failed: ${r.data.reason || "unknown"}`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    setSending(false);
  }

  const last = d?.last_run;
  return (
    <>
      <button data-testid={`${testid}-btn`} onClick={show} className="btn-ghost flex items-center gap-2"><FileBarChart size={14} strokeWidth={1.5} /> Daily report</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-serif-strong">{title}</DialogTitle>
            <DialogDescription>Sent automatically at 09:00 UK time every day. {note}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between gap-3 text-xs flex-wrap">
            <div className="text-[var(--ink-2)]" data-testid={`${testid}-meta`}>
              To: <b className="text-[var(--ink)]">{d?.to?.join(", ") || "…"}</b>
              <span className="mx-2">·</span>
              Last sent: {last ? <span data-testid={`${testid}-last`}>{new Date(last.ran_at).toLocaleString("en-GB")} {last.mail_ok ? "✓" : <span className="text-[var(--err)]">failed ({last.mail_reason})</span>}</span> : "never"}
            </div>
            <div className="flex gap-2">
              <button data-testid={`${testid}-refresh`} onClick={load} className="btn-ghost !py-1.5 !px-2.5 text-xs flex items-center gap-1"><RefreshCw size={12} /> Refresh</button>
              <button data-testid={`${testid}-resend`} onClick={resend} disabled={!d || sending} className="btn-primary !py-1.5 !px-3 text-xs flex items-center gap-1.5">
                {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} {sending ? "Sending…" : "Resend now"}
              </button>
            </div>
          </div>
          <div className="border border-[var(--line)] rounded-sm bg-white h-[60vh] overflow-hidden">
            {d ? <iframe title={title} srcDoc={`<body style="margin:16px">${d.html}</body>`} className="w-full h-full" data-testid={`${testid}-frame`} />
              : <div className="h-full flex items-center justify-center"><Loader2 className="animate-spin text-[var(--navy)]" /></div>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
